<script setup lang="ts">
import { computed, nextTick, onMounted, ref, type ComputedRef, type Ref } from 'vue';
// import { useI18n } from 'vue-i18n'
// import type { MessageSchema } from '@/i18n/index.js'
import ButtonClose from '@/assets/icons/Buttons/ButtonClose.vue';
import ButtonConfirm from '@/assets/icons/Buttons/ButtonConfirm.vue';
import ButtonMenu from '@/assets/icons/Buttons/ButtonMenu.vue';
import MyRecipesMenuItem from '@/components/MyRecipes/MyRecipesMenuItem.vue';
import { useDishes } from '@/composables/useDishes';
import { useEventBus } from '@/composables/useEventBus';
import { useFetch } from '@/composables/useFetch';
import { useOnboarding } from '@/composables/useOnboarding';
import type { MessageSchema } from '@/i18n';
import { useDishesStore } from '@/stores/dishesStore';
import { useEnvStore } from '@/stores/envStore';
import { useUserStore } from '@/stores/useUserStore';
import type { DishTag } from '@/types/api';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Menu from 'primevue/menu';
import Textarea from 'primevue/textarea';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

const openDishTagModal: Ref<boolean> = ref(false);
const newTagName: Ref<string> = ref('');
// const myDishTags: Ref<DishTag[] | []> = ref([])
const { fetchData } = useFetch();
const envStore = useEnvStore();
const toast = useToast();
const user = ref(useUserStore().user);
const dishesStore = useDishesStore();
const fetchingTags = computed(() => dishesStore.fetchingTags);
const restaurantId = user.value?.restaurants[0]?.id || null;
const route = useRoute();
const router = useRouter();
const parentId: Ref<string | string[] | null> = ref(route.params.parentId as string | null);
const confirmDialog = useConfirm();
const tagOptions = ref();
const isEditingTag = ref(false);
const onboarding = useOnboarding();
const { isOnboardingActive } = onboarding;

// Surveiller l'état d'onboarding et nettoyer les tags DB quand l'onboarding devient actif
watch(isOnboardingActive, (newValue) => {
  if (newValue) {
    console.log('[DishTagsView] Onboarding activé - nettoyage des tags DB');
    dishesStore.initOnboardingMode();
  }
});

// Update parentId when route changes
watch(
  () => router.currentRoute.value.params.parentId,
  (newParentId) => {
    console.log('newParentId ', newParentId);
    parentId.value = newParentId || null;
  },
);

// Add computed property to check if current tag is 'uncategorized'
const isUncategorizedTag = computed(() => {
  const parentTag = dishesStore.tags.find((tag) => tag.id.toString() === parentId.value);
  return parentTag?.name === 'uncategorized';
});

const selectedTag: ComputedRef<DishTag | undefined> = computed(() => {
  return dishesStore.tags.find((tag) => tag.id.toString() === parentId.value);
});
// Modify 'tagName' to translate 'uncategorized' tag name
const tagName = computed(() => {
  if (!parentId.value) return '';

  const currentTag = dishesStore.tags.find((tag) => tag.id.toString() === parentId.value);
  if (!currentTag) return '';

  // Translate 'uncategorized' if needed
  const translatedCurrentName = currentTag.name === 'uncategorized' ? t('dishTag.uncategorized') : currentTag.name;

  if (currentTag.parent_dish_tag_id) {
    // If the current tag has a parent
    const parentTag = dishesStore.tags.find((tag) => tag.id === currentTag.parent_dish_tag_id);
    if (parentTag) {
      const translatedParentName = parentTag.name === 'uncategorized' ? t('dishTag.uncategorized') : parentTag.name;
      return `${translatedParentName} > ${translatedCurrentName}`;
    }
  }

  // If there's no parent, just return the current tag's name
  return translatedCurrentName;
});

const links = [
  {
    name: t('pageTitle.myFavorites'),
    icon: null,
    url: '/my-recipes/favorites',
  },
];

// Modify computed property myCategories to sort dish tags alphabetically by name.
const myCategories = computed(() => {
  if (parentId.value) {
    // If we have a parentId, find the parent tag and return its child tags sorted alphabetically
    const parentTag = dishesStore.tags.find((tag) => tag.id.toString() === parentId.value);
    return [...(parentTag?.child_tags || [])]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((tag) => ({
        name: tag.name === 'uncategorized' ? t('dishTag.uncategorized') : tag.name,
        icon: null,
        url: `/my-recipes/tags/${parentId.value}/${tag.id}`,
      }));
  }
  // If no parentId, return top-level tags sorted alphabetically
  return [...dishesStore.tags]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((tag) => ({
      name: tag.name,
      icon: null,
      url: `/my-recipes/tags/${tag.id}`,
    }));
});

async function addDishTag() {
  if (newTagName.value.trim() !== '') {
    const newTag = {
      name: newTagName.value.trim(),
      parent_dish_tag_id: parentId.value ? parseInt(parentId.value.toString()) : null,
    };

    // Vérifier si on est en mode onboarding
    if (isOnboardingActive.value) {
      console.log('[Onboarding] Création de tag en local:', newTag);

      // Créer le tag localement via le store
      const completeTag = await dishesStore.editTag(newTag as DishTag, restaurantId, true);

      if (completeTag) {
        // Attendre que le DOM soit à jour avant d'émettre l'event mitt
        await nextTick();
        useEventBus().emit('onboarding:last-folder-added');
      }

      openDishTagModal.value = false;
      return;
    }

    try {
      const payload = {
        tags: [newTag],
      };
      const response = await fetchData(`${envStore.apiUrl}/dishes/tags/${restaurantId}`, 'POST', payload);

      if (response.data) {
        const createdTag = response.data.tags[0];

        if (createdTag.parent_dish_tag_id) {
          // If it's a child tag, find the parent and add to its child_tags
          const parentTag = dishesStore.tags.find((tag) => tag.id === createdTag.parent_dish_tag_id);
          if (parentTag) {
            parentTag.child_tags = parentTag.child_tags || [];
            parentTag.child_tags.push(createdTag);
          }
        } else {
          // If it's a parent tag, add to root tags
          dishesStore.tags = [...dishesStore.tags, createdTag];
        }

        // Attend que le DOM soit à jour avant d'émettre l'event mitt
        await nextTick();
        useEventBus().emit('onboarding:last-folder-added');

        // toast.add({
        //   severity: 'success',
        //   summary: t('common.success'),
        //   detail: t('dishTag.add.success'),
        //   life: 3000,
        // });
      } else if (response.error) {
        toast.add({
          severity: 'error',
          summary: t('common.error'),
          detail: response.error.message || response.error,
          life: 3000,
        });
      }
    } catch (error) {
      console.error('Error adding dish tag:', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('dishTag.add.error'),
        life: 3000,
      });
    }
  }

  openDishTagModal.value = false;
}

const toggleTagOptions = (event: Event) => {
  tagOptions.value.toggle(event);
};

// Replace the static tagOptionsItem ref with a computed property
const tagOptionsItem = computed(() => [
  {
    items: [
      {
        label: t('menuDishView.topMenu.rename'),
        icon: 'pi pi-pencil',
        command: () => {
          newTagName.value = tagName.value;
          isEditingTag.value = true;
        },
        disabled: isUncategorizedTag.value,
      },
      {
        label: t('menuDishView.topMenu.delete'),
        icon: 'pi pi-trash',
        command: () => confirmDeleteTag(),
        disabled: isUncategorizedTag.value,
      },
      ...(user.value?.role === 'admin' && !isUncategorizedTag.value && !selectedTag.value?.parent_dish_tag_id
        ? [
            {
              label: selectedTag.value?.is_template ? t('dish.removeTemplate') : t('dish.setTemplate'),
              icon: selectedTag.value?.is_template ? 'pi pi-star-fill' : 'pi pi-star',
              command: async () => {
                const isSuccess = await useDishes().setDishTagTemplate(parseInt(parentId.value as string), toast);
                if (isSuccess) {
                  const tagToEdit = dishesStore.tags.find((tag) => tag.id.toString() === parentId.value);
                  if (tagToEdit) {
                    tagToEdit.is_template = !tagToEdit.is_template;
                  }
                }
              },
            },
          ]
        : []),
    ],
  },
]);

const confirmDeleteTag = () => {
  confirmDialog.require({
    message: t('dishTag.delete.confirmDelete'),
    header: t('dishTag.delete.confirmDelete'),
    icon: 'pi pi-exclamation-triangle',
    rejectClass: 'p-button-secondary p-button-outlined',
    rejectLabel: t('common.cancel'),
    acceptLabel: t('common.delete'),
    accept: () => {
      deleteTag();
    },
    reject: () => {},
  });
};

// Prevent deleting the 'uncategorized' tag
async function deleteTag() {
  if (!parentId.value) return;
  const tagToDelete = dishesStore.tags.find((tag) => tag.id.toString() === parentId.value);
  if (!tagToDelete || tagToDelete.name === 'uncategorized') return;

  await dishesStore.deleteTag(parseInt(parentId.value.toString()), restaurantId);
  router.push('/my-recipes/tags');
}

// Prevent editing the 'uncategorized' tag
async function editTag() {
  if (!parentId.value) return;

  const tagToEdit = dishesStore.tags.find((tag) => tag.id.toString() === parentId.value);
  if (!tagToEdit || tagToEdit.name === 'uncategorized') return;

  tagToEdit.name = newTagName.value;
  await dishesStore.editTag(tagToEdit, restaurantId, false);
  isEditingTag.value = false;
}

onMounted(async () => {
  // Si l'onboarding est actif, forcer l'initialisation du mode onboarding
  if (isOnboardingActive.value) {
    console.log('[DishTagsView] onMounted avec onboarding actif - initialisation du mode onboarding');
    dishesStore.initOnboardingMode();
    return;
  }

  // Sinon, charger normalement si pas de tags
  if (dishesStore.tags.length === 0) {
    dishesStore.fetchTags(restaurantId, toast, false);
  }
});

watch(openDishTagModal, (newValue) => {
  if (!newValue) {
    newTagName.value = '';
  }
});

function handleCancel() {
  openDishTagModal.value = false;
  // Émet l'événement pour l'onboarding pour revenir en arrière
  useEventBus().emit('onboarding:dialog-canceled-by-user');
}

function onDialogVisibilityChange(isVisible: boolean) {
  // Si le dialog se ferme (isVisible === false) ET que l'onboarding est actif
  if (!isVisible && onboarding.isOnboardingActive.value) {
    console.log('[Onboarding] Dialog fermé par la croix ou mask → Cancel');
    useEventBus().emit('onboarding:dialog-canceled-by-user');
  }
}
</script>

<template>
  <main>
    <div v-if="!parentId">
      <MyRecipesMenuItem
        v-for="(link, index) in links"
        :key="link.name"
        :name="link.name"
        :icon="link?.icon ?? undefined"
        :url="link.url"
        :index="index"
      />
    </div>
    <div v-else>
      <div v-if="!isEditingTag" class="flex items-center justify-between gap-2">
        <div class="flex flex-row items-center gap-2">
          <h1 class="text-2xl">{{ tagName }}</h1>
        </div>
        <Menu
          id="overlay_menu"
          ref="tagOptions"
          :model="tagOptionsItem"
          :popup="true"
          :pt="{ submenuHeader: { class: '!p-0' } }"
          :pt-options="{ mergeProps: true }"
        />
        <Button link rounded :pt="{ root: { class: '!p-1.5 !w-8 !h-8' } }" :pt-options="{ mergeProps: true }" @click="toggleTagOptions">
          <template #icon>
            <ButtonMenu />
          </template>
        </Button>
      </div>
      <div v-else class="items-top flex justify-between gap-2">
        <Textarea v-model="newTagName" class="w-full" row="2" />
        <Button link rounded :pt="{ root: { class: '!p-1.5 !w-8 !h-8' } }" :pt-options="{ mergeProps: true }" @click="isEditingTag = false">
          <template #icon>
            <ButtonClose />
          </template>
        </Button>
        <Button link rounded :pt="{ root: { class: '!py-1.5 !px-0 !w-12 !h-8' } }" :pt-options="{ mergeProps: true }" @click="editTag">
          <template #icon>
            <ButtonConfirm />
          </template>
        </Button>
      </div>
    </div>
    <div class="onboarding-folders-section my-7">
      <h1 v-if="!parentId" class="text-2xl">{{ t('homeView.myRecipes.myCategories') }}</h1>
      <div class="onboarding-tags-list mt-4">
        <p v-if="fetchingTags" class="mt-4 italic">{{ t('common.loading') }}</p>
        <p v-else-if="!fetchingTags && myCategories.length === 0" class="mt-4 italic">
          {{ t('dishTag.noTags') }}
        </p>
        <MyRecipesMenuItem
          v-for="(link, index) in myCategories"
          v-else
          :key="link.name"
          :name="link.name"
          :icon="link?.icon ?? undefined"
          :url="link.url"
          :index="index"
          :class="`onboarding-folder-item ${index === myCategories.length - 1 ? 'onboarding-last-folder' : ''}`"
        />
      </div>
      <!-- <Button @click="null" :label="t('common.seeMore')" /> -->
      <div class="mt-4">
        <p class="onboarding-add-folder inline-button mb-10" @click="openDishTagModal = true">+ {{ t('dishTag.add.label') }}</p>
      </div>
    </div>
    <Dialog
      v-model:visible="openDishTagModal"
      class="custom-dish-tag-dialog"
      modal
      :header="t('dishTag.add.label')"
      :style="{ width: '25rem', maxWidth: '90vw' }"
      :draggable="!isOnboardingActive"
      @update:visible="onDialogVisibilityChange"
    >
      <div v-if="openDishTagModal" class="mb-4 flex items-center gap-4">
        <label for="dishTag" class="w-24 font-semibold">{{ t('dishTag.tagName') }}</label>
        <InputText id="dishTag" v-model="newTagName" class="flex-auto" autocomplete="off" />
      </div>
      <div class="flex justify-between gap-2">
        <Button
          id="custom-dish-tag-dialog-button-cancel"
          class="custom-dish-tag-dialog-button-cancel"
          type="button"
          :label="t('common.cancel')"
          outlined
          :disabled="false"
          @click="handleCancel"
        ></Button>
        <div class="flex justify-end gap-2">
          <Button
            id="custom-dish-tag-dialog-button-save"
            class="custom-dish-tag-dialog-button-save"
            type="button"
            :label="t('common.save')"
            :disabled="false"
            @click="addDishTag()"
          ></Button>
        </div>
      </div>
    </Dialog>
  </main>
</template>
